/**
 * EngineeringModules.tsx - Główny interfejs modułów inżynierskich
 * PlexiSystem Engineering Module
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator, Shield, Package, Thermometer, Users, FileCheck,
  Settings, AlertTriangle, CheckCircle, Info, Download, Eye,
  ChevronRight, Wrench, Layers, DollarSign, Clock, Zap,
  BarChart3, Lightbulb, FileText, Database, HelpCircle
} from 'lucide-react';

// Import modułów obliczeniowych
import {
  calcPlateStrength,
  calcFasteners,
  checkComplianceModule,
  calculatePackaging,
  planThermalProcess,
  assessErgonomics,
  PlateInput,
  FastenerInput,
  ComplianceInput,
  PackagingInput,
  ThermalInput,
  ErgonomicsInput
} from '../calc';

// Import stałych
import { MATERIALS, MaterialKey } from '../constants/materials';
import { UseCase } from '../constants/standards';

// Import generatora PDF
import { EngineeringPDFGenerator } from './EngineeringPDFGenerator';

interface TabProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  badge?: string;
}

const Tab: React.FC<TabProps> = ({ label, icon, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all relative ${
      active
        ? 'bg-orange-500 text-white shadow-lg'
        : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
    }`}
  >
    {icon}
    <span className="font-medium">{label}</span>
    {badge && (
      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
        {badge}
      </span>
    )}
  </button>
);

type ModuleType = 'plate' | 'fastener' | 'compliance' | 'packaging' | 'thermal' | 'ergonomics';

const getModuleTitle = (module: ModuleType): string => {
  const titles: Record<ModuleType, string> = {
    plate: 'wytrzymałości płyt',
    fastener: 'doboru mocowań',
    compliance: 'zgodności z normami',
    packaging: 'projektowania opakowań',
    thermal: 'procesów termicznych',
    ergonomics: 'ergonomii ekspozytorów'
  };
  return titles[module];
};

export const EngineeringModules: React.FC = () => {
  const [activeModule, setActiveModule] = useState<ModuleType>('plate');
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Record<ModuleType, any>>({
    plate: null,
    fastener: null,
    compliance: null,
    packaging: null,
    thermal: null,
    ergonomics: null
  });

  // Stan dla modułu płyt
  const [plateInput, setPlateInput] = useState<PlateInput>({
    a_mm: 300,
    b_mm: 500,
    h_mm: 5,
    material: 'PMMA',
    load: {
      type: 'uniform',
      q_kPa: 2.5,
      conservative: true
    },
    support: 'simply_supported',
    deflection_limit_ratio: 200,
    safety_factor: 2.5,
    long_term: false
  });

  // Stan dla modułu mocowań
  const [fastenerInput, setFastenerInput] = useState<FastenerInput>({
    mass_kg: 10,
    dynamic: false,
    material: 'PMMA',
    thickness_mm: 10,
    joint_type: 'fixed'
  });

  // Stan dla modułu zgodności
  const [complianceInput, setComplianceInput] = useState<ComplianceInput>({
    use_case: 'pos_display',
    material: 'PMMA',
    thickness_mm: 5,
    edges_radius_mm: 2,
    uv_stabilized: false,
    food_contact: false
  });

  // Stan dla modułu pakowania
  const [packagingInput, setPackagingInput] = useState<PackagingInput>({
    product_dims_mm: [300, 200, 150],
    product_mass_kg: 2,
    route: 'domestic',
    fragility: 'medium',
    quantity: 1,
    fefco_type: '0201',
    custom_price_per_m2: 8.0
  });

  // Stan dla modułu termicznego
  const [thermalInput, setThermalInput] = useState<ThermalInput>({
    material: 'PMMA',
    thickness_mm: 5,
    process: 'bend',
    dimensions: {
      length_mm: 1000,
      bend_radius_mm: 15
    },
    temperature_delta_C: 150
  });

  // Stan dla modułu ergonomii
  const [ergonomicsInput, setErgonomicsInput] = useState<ErgonomicsInput>({
    use: 'fsdu',
    dimensions: {
      height_cm: 150,
      width_cm: 60,
      depth_cm: 40
    },
    shelf_count: 4,
    target_products: [
      { name: 'Produkt Premium', importance: 'high', height_cm: 140 },
      { name: 'Produkt Standard', importance: 'medium', height_cm: 100 }
    ]
  });

  // Automatyczne obliczenia przy zmianie danych
  useEffect(() => {
    calculateCurrentModule();
  }, [activeModule, plateInput, plateInput.h_mm, fastenerInput, complianceInput, packagingInput, thermalInput, ergonomicsInput]);

  const calculateCurrentModule = async () => {
    setIsCalculating(true);
    setError(null);
    
    try {
      let result;
      switch (activeModule) {
        case 'plate':
          result = calcPlateStrength(plateInput);
          break;
        case 'fastener':
          result = calcFasteners(fastenerInput);
          break;
        case 'compliance':
          result = checkComplianceModule(complianceInput);
          break;
        case 'packaging':
          result = calculatePackaging(packagingInput);
          break;
        case 'thermal':
          result = planThermalProcess(thermalInput);
          break;
        case 'ergonomics':
          result = assessErgonomics(ergonomicsInput);
          break;
      }
      
      setResults(prev => ({
        ...prev,
        [activeModule]: result
      }));
    } catch (err: any) {
      setError(err.message || 'Błąd podczas obliczeń');
    } finally {
      setIsCalculating(false);
    }
  };

  const generatePDF = () => {
    const currentResult = results[activeModule];
    if (!currentResult) return;

    const generator = new EngineeringPDFGenerator();
    generator.generateModuleReport(
      activeModule,
      getInputForModule(activeModule),
      currentResult
    );
  };

  const getInputForModule = (module: ModuleType) => {
    switch (module) {
      case 'plate': return plateInput;
      case 'fastener': return fastenerInput;
      case 'compliance': return complianceInput;
      case 'packaging': return packagingInput;
      case 'thermal': return thermalInput;
      case 'ergonomics': return ergonomicsInput;
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Calculator className="w-8 h-8 text-orange-500" />
            Moduły Inżynierskie PlexiSystem
          </h1>
          <p className="text-gray-400 mt-2">
            Kompleksowe obliczenia konstrukcyjne i projektowe
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Tab
            label="Wytrzymałość płyt"
            icon={<Layers className="w-4 h-4" />}
            active={activeModule === 'plate'}
            onClick={() => setActiveModule('plate')}
          />
          <Tab
            label="Mocowania"
            icon={<Wrench className="w-4 h-4" />}
            active={activeModule === 'fastener'}
            onClick={() => setActiveModule('fastener')}
          />
          <Tab
            label="Normy/BHP"
            icon={<Shield className="w-4 h-4" />}
            active={activeModule === 'compliance'}
            onClick={() => setActiveModule('compliance')}
            badge={results.compliance && !results.compliance.compliant ? '!' : undefined}
          />
          <Tab
            label="Pakowanie"
            icon={<Package className="w-4 h-4" />}
            active={activeModule === 'packaging'}
            onClick={() => setActiveModule('packaging')}
          />
          <Tab
            label="Procesy termiczne"
            icon={<Thermometer className="w-4 h-4" />}
            active={activeModule === 'thermal'}
            onClick={() => setActiveModule('thermal')}
          />
          <Tab
            label="Ergonomia POS"
            icon={<Users className="w-4 h-4" />}
            active={activeModule === 'ergonomics'}
            onClick={() => setActiveModule('ergonomics')}
          />
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-300">
            {error}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Input */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-zinc-800 rounded-xl p-6 shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-500" />
                Parametry {getModuleTitle(activeModule)}
              </h2>

              {/* Plate Module Inputs */}
              {activeModule === 'plate' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Materiał
                    </label>
                    <select
                      value={plateInput.material}
                      onChange={(e) => setPlateInput({...plateInput, material: e.target.value as MaterialKey})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    >
                      {Object.keys(MATERIALS).map(key => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        A [mm]
                      </label>
                      <input
                        type="number"
                        value={plateInput.a_mm}
                        onChange={(e) => setPlateInput({...plateInput, a_mm: Number(e.target.value)})}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        min="10"
                        max="5000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        B [mm]
                      </label>
                      <input
                        type="number"
                        value={plateInput.b_mm}
                        onChange={(e) => setPlateInput({...plateInput, b_mm: Number(e.target.value)})}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        min="10"
                        max="5000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        H [mm]
                      </label>
                      <input
                        type="number"
                        value={plateInput.h_mm}
                        onChange={(e) => setPlateInput({...plateInput, h_mm: Number(e.target.value)})}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        min="1"
                        max="50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Typ obciążenia
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPlateInput({
                          ...plateInput,
                          load: { type: 'uniform', q_kPa: 2.5 }
                        })}
                        className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                          plateInput.load.type === 'uniform'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Równomierne
                      </button>
                      <button
                        onClick={() => setPlateInput({
                          ...plateInput,
                          load: { type: 'hydrostatic', H_mm: 1000, density_kg_m3: 1000, conservative: true }
                        })}
                        className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                          plateInput.load.type === 'hydrostatic'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Hydrostatyczne
                      </button>
                      <button
                        onClick={() => setPlateInput({
                          ...plateInput,
                          load: { type: 'point', F_N: 100 }
                        })}
                        className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                          plateInput.load.type === 'point'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Punktowe
                      </button>
                    </div>
                  </div>

                  {plateInput.load.type === 'uniform' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Obciążenie [kPa]
                      </label>
                      <input
                        type="number"
                        value={plateInput.load.q_kPa || 0}
                        onChange={(e) => setPlateInput({
                          ...plateInput,
                          load: { ...plateInput.load, q_kPa: Number(e.target.value) }
                        })}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        step="0.1"
                        placeholder="np. 2.5"
                      />
                    </div>
                  ) : plateInput.load.type === 'hydrostatic' ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Wysokość słupa cieczy [mm]
                        </label>
                        <input
                          type="number"
                          value={plateInput.load.H_mm || 1000}
                          onChange={(e) => setPlateInput({
                            ...plateInput,
                            load: { ...plateInput.load, H_mm: Number(e.target.value) }
                          })}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                          step="10"
                          placeholder="np. 1000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Gęstość cieczy [kg/m³]
                        </label>
                        <select
                          value={plateInput.load.density_kg_m3 || 1000}
                          onChange={(e) => setPlateInput({
                            ...plateInput,
                            load: { ...plateInput.load, density_kg_m3: Number(e.target.value) }
                          })}
                          className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        >
                          <option value="1000">Woda (1000)</option>
                          <option value="800">Olej (800)</option>
                          <option value="1200">Roztwór soli (1200)</option>
                          <option value="1500">Syrop (1500)</option>
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={plateInput.load.conservative !== false}
                            onChange={(e) => setPlateInput({
                              ...plateInput,
                              load: { ...plateInput.load, conservative: e.target.checked }
                            })}
                            className="rounded border-zinc-600 bg-zinc-700 text-orange-500"
                          />
                          <span className="text-sm text-gray-300">Obliczenia konserwatywne (pełne ciśnienie)</span>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Siła [kg]
                        <span className="text-xs text-gray-500 ml-2">(przeliczane na N)</span>
                      </label>
                      <input
                        type="number"
                        value={Math.round((plateInput.load as any).F_N / 9.81 * 10) / 10}
                        onChange={(e) => setPlateInput({
                          ...plateInput,
                          load: { ...plateInput.load, F_N: Number(e.target.value) * 9.81 }
                        })}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        step="0.1"
                        placeholder="np. 10"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Warunki brzegowe
                    </label>
                    <select
                      value={plateInput.support}
                      onChange={(e) => setPlateInput({...plateInput, support: e.target.value as any})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                    >
                      <option value="simply_supported">Swobodnie podparte</option>
                      <option value="fixed">Utwierdzone</option>
                      <option value="mixed">Mieszane (2 utwierdzone, 2 podparte)</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={plateInput.long_term || false}
                        onChange={(e) => setPlateInput({...plateInput, long_term: e.target.checked})}
                        className="rounded border-zinc-600 bg-zinc-700 text-orange-500"
                      />
                      <span className="text-sm text-gray-300">Obciążenie długotrwałe (redukcja o 40%)</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Fastener Module Inputs */}
              {activeModule === 'fastener' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Masa elementu [kg]
                    </label>
                    <input
                      type="number"
                      value={fastenerInput.mass_kg}
                      onChange={(e) => setFastenerInput({...fastenerInput, mass_kg: Number(e.target.value)})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Typ połączenia
                    </label>
                    <select
                      value={fastenerInput.joint_type}
                      onChange={(e) => setFastenerInput({...fastenerInput, joint_type: e.target.value as any})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                    >
                      <option value="fixed">Stałe</option>
                      <option value="hinge">Zawiasowe</option>
                      <option value="removable">Rozłączne</option>
                      <option value="sliding">Przesuwne</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={fastenerInput.dynamic}
                        onChange={(e) => setFastenerInput({...fastenerInput, dynamic: e.target.checked})}
                        className="rounded border-zinc-600 bg-zinc-700 text-orange-500"
                      />
                      <span className="text-sm text-gray-300">Obciążenia dynamiczne</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Compliance Module Inputs */}
              {activeModule === 'compliance' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Zastosowanie
                    </label>
                    <select
                      value={complianceInput.use_case}
                      onChange={(e) => setComplianceInput({...complianceInput, use_case: e.target.value as UseCase})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                    >
                      <option value="machine_guard">Osłony maszyn</option>
                      <option value="food_contact">Kontakt z żywnością</option>
                      <option value="pos_display">Ekspozytory POS</option>
                      <option value="uv_heat_resistance">Odporność UV/temp</option>
                      <option value="medical">Medyczne</option>
                      <option value="cleanroom">Pomieszczenia czyste</option>
                      <option value="explosion_proof">Przeciwwybuchowe</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Promień krawędzi [mm]
                    </label>
                    <input
                      type="number"
                      value={complianceInput.edges_radius_mm}
                      onChange={(e) => setComplianceInput({...complianceInput, edges_radius_mm: Number(e.target.value)})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                      min="0"
                      max="10"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={complianceInput.uv_stabilized}
                        onChange={(e) => setComplianceInput({...complianceInput, uv_stabilized: e.target.checked})}
                        className="rounded border-zinc-600 bg-zinc-700 text-orange-500"
                      />
                      <span className="text-sm text-gray-300">Stabilizacja UV</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={complianceInput.food_contact}
                        onChange={(e) => setComplianceInput({...complianceInput, food_contact: e.target.checked})}
                        className="rounded border-zinc-600 bg-zinc-700 text-orange-500"
                      />
                      <span className="text-sm text-gray-300">Atest do kontaktu z żywnością</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Packaging Module Inputs */}
              {activeModule === 'packaging' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Wymiary produktu [mm]
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="number"
                        value={packagingInput.product_dims_mm[0]}
                        onChange={(e) => {
                          const dims = [...packagingInput.product_dims_mm];
                          dims[0] = Number(e.target.value);
                          setPackagingInput({...packagingInput, product_dims_mm: dims as [number, number, number]});
                        }}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        placeholder="Długość"
                      />
                      <input
                        type="number"
                        value={packagingInput.product_dims_mm[1]}
                        onChange={(e) => {
                          const dims = [...packagingInput.product_dims_mm];
                          dims[1] = Number(e.target.value);
                          setPackagingInput({...packagingInput, product_dims_mm: dims as [number, number, number]});
                        }}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        placeholder="Szerokość"
                      />
                      <input
                        type="number"
                        value={packagingInput.product_dims_mm[2]}
                        onChange={(e) => {
                          const dims = [...packagingInput.product_dims_mm];
                          dims[2] = Number(e.target.value);
                          setPackagingInput({...packagingInput, product_dims_mm: dims as [number, number, number]});
                        }}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        placeholder="Wysokość"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Masa [kg]
                      </label>
                      <input
                        type="number"
                        value={packagingInput.product_mass_kg}
                        onChange={(e) => setPackagingInput({...packagingInput, product_mass_kg: Number(e.target.value)})}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Ilość sztuk
                      </label>
                      <input
                        type="number"
                        value={packagingInput.quantity || 1}
                        onChange={(e) => setPackagingInput({...packagingInput, quantity: Number(e.target.value)})}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        min="1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Typ kartonu FEFCO
                    </label>
                    <select
                      value={packagingInput.fefco_type || '0201'}
                      onChange={(e) => setPackagingInput({...packagingInput, fefco_type: e.target.value})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                    >
                      <option value="0201">FEFCO 0201 - Klapówka zwykła</option>
                      <option value="0203">FEFCO 0203 - Klapówka z zakładkami</option>
                      <option value="0204">FEFCO 0204 - Klapówka z pełnymi zakładkami</option>
                      <option value="0215">FEFCO 0215 - Klapówka z zamkiem</option>
                      <option value="0300">FEFCO 0300 - Pudło teleskopowe</option>
                      <option value="0301">FEFCO 0301 - Pudło z wiekiem</option>
                      <option value="0410">FEFCO 0410 - Tacka</option>
                      <option value="0421">FEFCO 0421 - Tacka narożna</option>
                      <option value="0427">FEFCO 0427 - Tacka z pokrywą</option>
                      <option value="0700">FEFCO 0700 - Pudło gotowe do klejenia</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Cena tektury z robocizną [zł/m²]
                    </label>
                    <input
                      type="number"
                      value={packagingInput.custom_price_per_m2 || 8.0}
                      onChange={(e) => setPackagingInput({...packagingInput, custom_price_per_m2: Number(e.target.value)})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                      step="0.5"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Trasa transportu
                    </label>
                    <select
                      value={packagingInput.route}
                      onChange={(e) => setPackagingInput({...packagingInput, route: e.target.value as any})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                    >
                      <option value="local">Lokalny (do 50km)</option>
                      <option value="domestic">Krajowy</option>
                      <option value="intl">Międzynarodowy</option>
                      <option value="air">Lotniczy</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Delikatność produktu
                    </label>
                    <div className="flex gap-2">
                      {['low', 'medium', 'high'].map(level => (
                        <button
                          key={level}
                          onClick={() => setPackagingInput({...packagingInput, fragility: level as any})}
                          className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                            packagingInput.fragility === level
                              ? 'bg-orange-500 text-white'
                              : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                          }`}
                        >
                          {level === 'low' ? 'Niska' : level === 'medium' ? 'Średnia' : 'Wysoka'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Thermal Module Inputs */}
              {activeModule === 'thermal' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Proces
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setThermalInput({...thermalInput, process: 'bend'})}
                        className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                          thermalInput.process === 'bend'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Gięcie
                      </button>
                      <button
                        onClick={() => setThermalInput({...thermalInput, process: 'thermo'})}
                        className={`flex-1 px-3 py-2 rounded-lg transition-all ${
                          thermalInput.process === 'thermo'
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Termoformowanie
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Materiał
                    </label>
                    <select
                      value={thermalInput.material}
                      onChange={(e) => setThermalInput({...thermalInput, material: e.target.value as MaterialKey})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                    >
                      {Object.keys(MATERIALS).map(key => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Grubość [mm]
                    </label>
                    <input
                      type="number"
                      value={thermalInput.thickness_mm}
                      onChange={(e) => setThermalInput({...thermalInput, thickness_mm: Number(e.target.value)})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                      min="1"
                      max="20"
                    />
                  </div>

                  {thermalInput.process === 'bend' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Promień gięcia [mm]
                      </label>
                      <input
                        type="number"
                        value={thermalInput.dimensions?.bend_radius_mm || 0}
                        onChange={(e) => setThermalInput({
                          ...thermalInput,
                          dimensions: {...thermalInput.dimensions, bend_radius_mm: Number(e.target.value)}
                        })}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        min="1"
                        max="100"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Ergonomics Module Inputs */}
              {activeModule === 'ergonomics' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Typ ekspozytora
                    </label>
                    <select
                      value={ergonomicsInput.use}
                      onChange={(e) => setErgonomicsInput({...ergonomicsInput, use: e.target.value as any})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                    >
                      <option value="fsdu">FSDU (podłogowy)</option>
                      <option value="counter">Blatowy</option>
                      <option value="infoStand">Stojak informacyjny</option>
                      <option value="shelf">Regał</option>
                      <option value="bin">Pojemnik</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Wymiary [cm]
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="number"
                        value={ergonomicsInput.dimensions.height_cm}
                        onChange={(e) => setErgonomicsInput({
                          ...ergonomicsInput,
                          dimensions: {...ergonomicsInput.dimensions, height_cm: Number(e.target.value)}
                        })}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        placeholder="Wysokość"
                      />
                      <input
                        type="number"
                        value={ergonomicsInput.dimensions.width_cm}
                        onChange={(e) => setErgonomicsInput({
                          ...ergonomicsInput,
                          dimensions: {...ergonomicsInput.dimensions, width_cm: Number(e.target.value)}
                        })}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        placeholder="Szerokość"
                      />
                      <input
                        type="number"
                        value={ergonomicsInput.dimensions.depth_cm}
                        onChange={(e) => setErgonomicsInput({
                          ...ergonomicsInput,
                          dimensions: {...ergonomicsInput.dimensions, depth_cm: Number(e.target.value)}
                        })}
                        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                        placeholder="Głębokość"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Liczba półek
                    </label>
                    <input
                      type="number"
                      value={ergonomicsInput.shelf_count}
                      onChange={(e) => setErgonomicsInput({...ergonomicsInput, shelf_count: Number(e.target.value)})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                      min="1"
                      max="10"
                    />
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Right Panel - Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            {/* Plate Results */}
            {activeModule === 'plate' && results.plate && (
              <PlateResults result={results.plate} onGeneratePDF={generatePDF} />
            )}

            {/* Fastener Results */}
            {activeModule === 'fastener' && results.fastener && (
              <FastenerResults result={results.fastener} onGeneratePDF={generatePDF} />
            )}

            {/* Compliance Results */}
            {activeModule === 'compliance' && results.compliance && (
              <ComplianceResults result={results.compliance} onGeneratePDF={generatePDF} />
            )}

            {/* Packaging Results */}
            {activeModule === 'packaging' && results.packaging && (
              <PackagingResults result={results.packaging} onGeneratePDF={generatePDF} />
            )}

            {/* Thermal Results */}
            {activeModule === 'thermal' && results.thermal && (
              <ThermalResults result={results.thermal} onGeneratePDF={generatePDF} />
            )}

            {/* Ergonomics Results */}
            {activeModule === 'ergonomics' && results.ergonomics && (
              <ErgonomicsResults result={results.ergonomics} onGeneratePDF={generatePDF} />
            )}

            {/* Loading State */}
            {isCalculating && (
              <div className="bg-zinc-800 rounded-xl p-12 shadow-xl flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
                <p className="text-gray-400">Obliczanie...</p>
              </div>
            )}

            {/* Empty State */}
            {!isCalculating && !results[activeModule] && (
              <div className="bg-zinc-800 rounded-xl p-12 shadow-xl text-center">
                <Calculator className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Wprowadź parametry i wykonaj obliczenia</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Komponenty wyników dla każdego modułu
const PlateResults: React.FC<{ result: any; onGeneratePDF: () => void }> = ({ result, onGeneratePDF }) => (
  <div className="bg-zinc-800 rounded-xl p-6 shadow-xl">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-white flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-orange-500" />
        Wyniki analizy wytrzymałości
      </h2>
      <button
        onClick={onGeneratePDF}
        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Pobierz PDF
      </button>
    </div>

    {/* Status wykonywalności */}
    {!result.executable && (
      <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-red-500" />
          <span className="text-red-400 font-bold text-lg">KONSTRUKCJA NIEWYKONALNA</span>
        </div>
        <p className="text-sm text-red-300 mt-2">Konieczna zmiana konstrukcji lub zastosowanie żebrowania</p>
      </div>
    )}

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Ugięcie max</p>
        <p className="text-xl font-bold text-white">{result.w_max_mm.toFixed(2)} mm</p>
        <p className="text-xs text-gray-500 mt-1">Limit: {result.deflection_limit_mm.toFixed(2)} mm</p>
        <div className="mt-2 h-2 bg-zinc-600 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all ${
              result.utilization_deflection <= 1 ? 'bg-green-500' : 'bg-red-500'
            }`}
            style={{ width: `${Math.min(100, result.utilization_deflection * 100)}%` }}
          />
        </div>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Naprężenie max</p>
        <p className="text-xl font-bold text-white">{result.sigma_max_MPa.toFixed(1)} MPa</p>
        <p className="text-xs text-gray-500 mt-1">Dopuszczalne: {result.sigma_allow_MPa} MPa</p>
        <div className="mt-2 h-2 bg-zinc-600 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all ${
              result.utilization_stress <= 1 ? 'bg-green-500' : 'bg-red-500'
            }`}
            style={{ width: `${Math.min(100, result.utilization_stress * 100)}%` }}
          />
        </div>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Współczynnik bezp.</p>
        <p className="text-xl font-bold text-white">{result.safety_factor_actual.toFixed(1)}</p>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Status</p>
        <p className="text-xl font-bold">
          {result.pass && result.executable ? (
            <span className="text-green-500">SPEŁNIA</span>
          ) : (
            <span className="text-red-500">NIE SPEŁNIA</span>
          )}
        </p>
      </div>
    </div>

    {/* Ostrzeżenia */}
    {result.warnings && result.warnings.length > 0 && (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-red-300 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          Ostrzeżenia
        </h3>
        <ul className="space-y-1">
          {result.warnings.map((warning: string, idx: number) => (
            <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
              <span className="text-red-500">•</span>
              <span>{warning}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Żebrowanie */}
    {result.ribs && result.ribs.required && (
      <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-blue-300 mb-2 flex items-center gap-2">
          <Layers className="w-4 h-4 text-blue-500" />
          Zalecane żebrowanie
        </h3>
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <span className="text-gray-400">Liczba żeber:</span>
            <span className="text-white font-medium ml-2">{result.ribs.count}</span>
          </div>
          <div>
            <span className="text-gray-400">Rozstaw:</span>
            <span className="text-white font-medium ml-2">{result.ribs.spacing_mm} mm</span>
          </div>
          <div>
            <span className="text-gray-400">Wysokość żebra:</span>
            <span className="text-white font-medium ml-2">{result.ribs.height_mm} mm</span>
          </div>
          <div>
            <span className="text-gray-400">Grubość żebra:</span>
            <span className="text-white font-medium ml-2">{result.ribs.thickness_mm} mm</span>
          </div>
        </div>
      </div>
    )}

    {/* Zalecenia */}
    {result.recommendations.length > 0 && (
      <div className="bg-zinc-700 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-yellow-500" />
          Zalecenia
        </h3>
        <ul className="space-y-1">
          {result.recommendations.map((rec: string, idx: number) => (
            <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
              <span className="text-yellow-500">•</span>
              <span>{rec}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {result.h_recommended && (
      <div className="bg-orange-900/20 border border-orange-500/50 rounded-lg p-4">
        <p className="text-sm text-orange-300">
          Zalecana grubość: <span className="font-bold text-orange-400">{result.h_recommended} mm</span>
        </p>
      </div>
    )}

    {/* Szczegóły obliczeniowe */}
    {result.calculation_details && (
      <details className="mt-4">
        <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300">
          Szczegóły obliczeniowe
        </summary>
        <div className="mt-2 p-3 bg-zinc-700 rounded-lg text-xs text-gray-300 space-y-1">
          <div>Stosunek boków (a/b): {result.calculation_details.aspect_ratio.toFixed(3)}</div>
          <div>Współczynnik Cσ: {result.calculation_details.Csig.toFixed(4)}</div>
          <div>Współczynnik Cw: {result.calculation_details.Cw.toFixed(5)}</div>
          <div>Ciśnienie obliczeniowe: {(result.calculation_details.q_Pa/1000).toFixed(1)} kPa</div>
          <div>Moduł Younga: {(result.calculation_details.E_Pa/1e9).toFixed(1)} GPa</div>
        </div>
      </details>
    )}
  </div>
);

const FastenerResults: React.FC<{ result: any; onGeneratePDF: () => void }> = ({ result, onGeneratePDF }) => (
  <div className="bg-zinc-800 rounded-xl p-6 shadow-xl">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-white flex items-center gap-2">
        <Wrench className="w-5 h-5 text-orange-500" />
        Zalecane mocowania
      </h2>
      <button
        onClick={onGeneratePDF}
        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Pobierz PDF
      </button>
    </div>

    <div className="bg-zinc-700 rounded-lg p-4 mb-4">
      <h3 className="text-lg font-semibold text-white mb-2">
        {result.primary_method.type.replace(/_/g, ' ').toUpperCase()}
      </h3>
      <p className="text-sm text-gray-300 mb-2">
        Nośność: <span className="font-bold text-green-400">{result.primary_method.capacity_N} N</span>
      </p>
      {result.primary_method.details && (
        <div className="text-xs text-gray-400 space-y-1">
          {Object.entries(result.primary_method.details).map(([key, value]: [string, any]) => (
            <div key={key}>
              <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {String(value)}
            </div>
          ))}
        </div>
      )}
    </div>

    {result.bom && result.bom.length > 0 && (
      <div className="bg-zinc-700 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-white mb-2">Lista materiałów (BOM)</h3>
        <div className="space-y-2">
          {result.bom.map((item: any, idx: number) => (
            <div key={idx} className="flex justify-between text-xs">
              <span className="text-gray-300">{item.item}</span>
              <span className="text-white font-medium">{item.quantity} {item.unit || 'szt.'}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {result.assembly_notes && result.assembly_notes.length > 0 && (
      <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-300 mb-2">Uwagi montażowe</h3>
        <ul className="space-y-1">
          {result.assembly_notes.map((note: string, idx: number) => (
            <li key={idx} className="text-xs text-gray-300">{note}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const ComplianceResults: React.FC<{ result: any; onGeneratePDF: () => void }> = ({ result, onGeneratePDF }) => (
  <div className="bg-zinc-800 rounded-xl p-6 shadow-xl">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-white flex items-center gap-2">
        <Shield className="w-5 h-5 text-orange-500" />
        Zgodność z normami
      </h2>
      <button
        onClick={onGeneratePDF}
        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Pobierz PDF
      </button>
    </div>

    <div className={`p-4 rounded-lg mb-4 ${result.compliant ? 'bg-green-900/20 border border-green-500' : 'bg-red-900/20 border border-red-500'}`}>
      <div className="flex items-center gap-2">
        {result.compliant ? (
          <>
            <CheckCircle className="w-6 h-6 text-green-500" />
            <span className="text-green-400 font-semibold">ZGODNE Z NORMAMI</span>
          </>
        ) : (
          <>
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <span className="text-red-400 font-semibold">NIEZGODNE - WYMAGA KOREKT</span>
          </>
        )}
      </div>
    </div>

    {result.requirements_met && result.requirements_met.length > 0 && (
      <div className="space-y-2 mb-4">
        {result.requirements_met.map((req: any, idx: number) => (
          <div key={idx} className="flex items-center justify-between bg-zinc-700 rounded-lg p-3">
            <span className="text-sm text-gray-300">{req.requirement}</span>
            <div className="flex items-center gap-2">
              {req.status === 'pass' ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : req.status === 'warning' ? (
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-xs text-gray-400">{req.details}</span>
            </div>
          </div>
        ))}
      </div>
    )}

    {result.recommendations && result.recommendations.length > 0 && (
      <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-yellow-300 mb-2">Zalecenia</h3>
        <ul className="space-y-1">
          {result.recommendations.map((rec: string, idx: number) => (
            <li key={idx} className="text-xs text-gray-300">{rec}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const PackagingResults: React.FC<{ result: any; onGeneratePDF: () => void }> = ({ result, onGeneratePDF }) => (
  <div className="bg-zinc-800 rounded-xl p-6 shadow-xl">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-white flex items-center gap-2">
        <Package className="w-5 h-5 text-orange-500" />
        Projekt opakowania FEFCO
      </h2>
      <button
        onClick={onGeneratePDF}
        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Pobierz PDF
      </button>
    </div>

    {/* Typ kartonu FEFCO */}
    <div className="bg-orange-900/20 border border-orange-500/50 rounded-lg p-4 mb-4">
      <h3 className="text-lg font-semibold text-orange-300">{result.carton.fefco_type}</h3>
      <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
        <div className="text-gray-300">
          Powierzchnia: <span className="text-white font-medium">{result.carton.surface_area_m2} m²</span>
        </div>
        <div className="text-gray-300">
          Z odpadami: <span className="text-white font-medium">{result.carton.material_usage_m2} m²</span>
        </div>
      </div>
    </div>

    {/* Główne parametry */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Wymiary zewnętrzne</p>
        <p className="text-sm font-bold text-white">
          {result.carton.external_mm.map((d: number) => `${d}`).join('×')}mm
        </p>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Koszt jednostkowy</p>
        <p className="text-xl font-bold text-green-400">{result.cost_estimate.per_unit.toFixed(2)} zł</p>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Cena za m²</p>
        <p className="text-xl font-bold text-white">{result.cost_estimate.price_per_m2.toFixed(2)} zł</p>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Koszt całkowity</p>
        <p className="text-xl font-bold text-yellow-400">{result.cost_estimate.total.toFixed(2)} zł</p>
      </div>
    </div>

    {/* Rozkład kosztów */}
    <div className="bg-zinc-700 rounded-lg p-4 mb-4">
      <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-green-500" />
        Szczegółowa kalkulacja kosztów
      </h3>
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-gray-400">Karton (materiał + robocizna):</span>
          <span className="text-white font-medium">{result.cost_estimate.breakdown.carton.toFixed(2)} zł</span>
        </div>
        <div className="flex justify-between text-xs pl-4">
          <span className="text-gray-500">- Materiał:</span>
          <span className="text-gray-300">{result.cost_estimate.material_cost.toFixed(2)} zł</span>
        </div>
        <div className="flex justify-between text-xs pl-4">
          <span className="text-gray-500">- Robocizna:</span>
          <span className="text-gray-300">{result.cost_estimate.labor_cost.toFixed(2)} zł</span>
        </div>
        {result.cost_estimate.breakdown.foam > 0 && (
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Pianka ochronna:</span>
            <span className="text-white font-medium">{result.cost_estimate.breakdown.foam.toFixed(2)} zł</span>
          </div>
        )}
        {result.cost_estimate.breakdown.corners > 0 && (
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Narożniki:</span>
            <span className="text-white font-medium">{result.cost_estimate.breakdown.corners.toFixed(2)} zł</span>
          </div>
        )}
        <div className="flex justify-between text-xs">
          <span className="text-gray-400">Paleta (na sztukę):</span>
          <span className="text-white font-medium">{result.cost_estimate.breakdown.pallet.toFixed(2)} zł</span>
        </div>
        <div className="border-t border-zinc-600 pt-2 mt-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-300 font-medium">RAZEM:</span>
            <span className="text-green-400 font-bold">{result.cost_estimate.per_unit.toFixed(2)} zł</span>
          </div>
        </div>
      </div>
    </div>

    {/* Specyfikacja techniczna */}
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="bg-zinc-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-white mb-2">Parametry wytrzymałościowe</h3>
        <div className="space-y-1 text-xs text-gray-300">
          <div>Klasa tektury: <span className="text-white">{result.carton.material_grade}</span></div>
          <div>BCT wymagane: <span className="text-white">{result.carton.BCT_required_N} N</span></div>
          <div>ECT wymagane: <span className="text-white">{result.carton.ECT_required_kN_m.toFixed(1)} kN/m</span></div>
          {result.carton.blank_dimensions_mm && (
            <div>Wykrój: <span className="text-white">
              {result.carton.blank_dimensions_mm[0]}×{result.carton.blank_dimensions_mm[1]}mm
            </span></div>
          )}
        </div>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-white mb-2">Parametry produkcyjne</h3>
        <div className="space-y-1 text-xs text-gray-300">
          <div>Cięcie: <span className="text-white">{result.production.cutting_length_m} m</span></div>
          <div>Bigowanie: <span className="text-white">{result.production.creasing_length_m} m</span></div>
          <div>Czas produkcji: <span className="text-white">{result.production.production_time_min} min</span></div>
          <div>Odpad: <span className="text-white">{result.production.waste_percent}%</span></div>
        </div>
      </div>
    </div>

    {/* Ochrona produktu */}
    <div className="bg-zinc-700 rounded-lg p-4 mb-4">
      <h3 className="text-sm font-semibold text-white mb-2">Zabezpieczenie produktu</h3>
      <div className="grid grid-cols-2 gap-4 text-xs text-gray-300">
        <div>
          <span>Pianka:</span> <span className="text-white">{result.protection.foam_type}</span>
        </div>
        <div>
          <span>Grubość:</span> <span className="text-white">{result.protection.foam_thickness_mm}mm</span>
        </div>
        <div>
          <span>Narożniki:</span> <span className="text-white">{result.protection.corner_protectors ? 'TAK' : 'NIE'}</span>
        </div>
        <div>
          <span>Folia bąbelkowa:</span> <span className="text-white">{result.protection.bubble_wrap ? 'TAK' : 'NIE'}</span>
        </div>
      </div>
    </div>

    {/* Paletyzacja */}
    <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4 mb-4">
      <h3 className="text-sm font-semibold text-blue-300 mb-2">Paletyzacja</h3>
      <div className="grid grid-cols-2 gap-4 text-xs">
        <div className="text-gray-300">
          Kartonów na palecie: <span className="text-white font-medium">{result.palletization.cartons_per_pallet}</span>
        </div>
        <div className="text-gray-300">
          Liczba warstw: <span className="text-white font-medium">{result.palletization.layers}</span>
        </div>
        <div className="text-gray-300">
          Układ: <span className="text-white font-medium">{result.palletization.arrangement}</span>
        </div>
        <div className="text-gray-300">
          Efektywność: <span className="text-white font-medium">{result.palletization.efficiency_percent}%</span>
        </div>
        <div className="text-gray-300">
          Waga palety: <span className="text-white font-medium">{result.palletization.pallet_weight_kg} kg</span>
        </div>
        <div className="text-gray-300">
          Waga całkowita: <span className="text-white font-medium">{result.palletization.total_weight_kg} kg</span>
        </div>
      </div>
    </div>

    {/* Oznaczenia transportowe */}
    {result.shipping_marks && result.shipping_marks.length > 0 && (
      <div className="bg-zinc-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-white mb-2">Oznaczenia transportowe</h3>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {result.shipping_marks.map((mark: string, idx: number) => (
            <div key={idx} className="text-gray-300 font-mono">{mark}</div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const ThermalResults: React.FC<{ result: any; onGeneratePDF: () => void }> = ({ result, onGeneratePDF }) => (
  <div className="bg-zinc-800 rounded-xl p-6 shadow-xl">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-white flex items-center gap-2">
        <Thermometer className="w-5 h-5 text-orange-500" />
        Plan procesu termicznego
      </h2>
      <button
        onClick={onGeneratePDF}
        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Pobierz PDF
      </button>
    </div>

    <div className="grid grid-cols-3 gap-4 mb-4">
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Temperatura</p>
        <p className="text-xl font-bold text-orange-400">{result.temperatures.selected_C}°C</p>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Czas całkowity</p>
        <p className="text-xl font-bold text-white">{result.timing.total_min} min</p>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Rozszerzalność</p>
        <p className="text-xl font-bold text-white">
          {result.expansion.deltaL_mm ? `${result.expansion.deltaL_mm.toFixed(1)} mm` : 'N/A'}
        </p>
      </div>
    </div>

    {result.process_schedule && result.process_schedule.length > 0 && (
      <div className="bg-zinc-700 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-500" />
          Harmonogram procesu
        </h3>
        <div className="space-y-1 text-xs text-gray-300 max-h-48 overflow-y-auto">
          {result.process_schedule.map((step: string, idx: number) => (
            <div key={idx}>{step}</div>
          ))}
        </div>
      </div>
    )}

    {result.warnings && result.warnings.length > 0 && (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-red-300 mb-2">Ostrzeżenia</h3>
        <ul className="space-y-1">
          {result.warnings.map((warning: string, idx: number) => (
            <li key={idx} className="text-xs text-gray-300">{warning}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const ErgonomicsResults: React.FC<{ result: any; onGeneratePDF: () => void }> = ({ result, onGeneratePDF }) => (
  <div className="bg-zinc-800 rounded-xl p-6 shadow-xl">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-white flex items-center gap-2">
        <Users className="w-5 h-5 text-orange-500" />
        Ocena ergonomii
      </h2>
      <button
        onClick={onGeneratePDF}
        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Pobierz PDF
      </button>
    </div>

    <div className="grid grid-cols-3 gap-4 mb-4">
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Ocena ogólna</p>
        <p className="text-2xl font-bold text-white">{result.overall_score}/100</p>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Golden zone</p>
        <p className="text-xl font-bold text-yellow-400">{result.golden_zone_usage.percentage.toFixed(0)}%</p>
      </div>
      <div className="bg-zinc-700 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Dostępność</p>
        <p className="text-xl font-bold text-green-400">{result.accessibility_score}/100</p>
      </div>
    </div>

    {result.visibility_analysis && result.visibility_analysis.length > 0 && (
      <div className="bg-zinc-700 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-white mb-2">Analiza widoczności półek</h3>
        <div className="space-y-2">
          {result.visibility_analysis.map((shelf: any, idx: number) => (
            <div key={idx} className="flex items-center justify-between text-xs">
              <span className="text-gray-300">Półka {shelf.shelf_number} ({shelf.height_cm}cm)</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">{shelf.zone}</span>
                <span className={`font-medium ${
                  shelf.visibility === 'excellent' ? 'text-green-400' :
                  shelf.visibility === 'good' ? 'text-yellow-400' :
                  shelf.visibility === 'fair' ? 'text-orange-400' :
                  'text-red-400'
                }`}>
                  {shelf.visibility}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {result.recommendations.general && result.recommendations.general.length > 0 && (
      <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-300 mb-2">Rekomendacje</h3>
        <ul className="space-y-1">
          {result.recommendations.general.map((rec: string, idx: number) => (
            <li key={idx} className="text-xs text-gray-300">{rec}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);
